/**
 * MYAC-919 — the questions worth asking about the thing you just clicked.
 *
 * Kept apart from `chatSuggestions.ts`, which mirrors the agent's TOOL FAMILIES
 * ("what can this assistant do at all"). These are about ONE object the user is
 * pointing at, so they are short, generic in wording, and they never repeat the
 * object's name — the chip above them already says which one it is.
 *
 * They all `send`: unlike a document-name prompt, there is nothing left for the
 * user to fill in. Sending is the credit-gated step and stays gated in
 * `ChatInterface`.
 *
 * One set per kind, because the useful question differs: a requirement is asked
 * about its verdict, a gap document about why it is expected, an action about
 * how to carry it out, an obligation about when it is next due.
 */
import type { ChatFocusRef } from '@/stores/chatbot'

/**
 * The chip's whole-task draft question — exported because the task dialog's
 * EMPTY Actions required asks exactly this (MYAC-1166): with no action to sit
 * beside, « ask the assistant to draft it » sends the chip's own sentence.
 * Defined above `BY_KIND` so the chip and the button read one literal.
 */
export const TASK_DRAFT_CONTENT_KEY = 'chat.focus.task.draft-content'

const BY_KIND: Record<string, string[]> = {
  requirement: [
    'chat.focus.requirement.why-rating',
    'chat.focus.requirement.evidence',
    'chat.focus.requirement.how-to-fix',
  ],
  document: [
    'chat.focus.document.why-expected',
    'chat.focus.document.what-content',
    'chat.focus.document.which-requirements',
  ],
  remediation: [
    'chat.focus.remediation.why-needed',
    'chat.focus.remediation.how-to-do',
    'chat.focus.remediation.which-documents',
  ],
  finding: [
    'chat.focus.finding.why-contradiction',
    'chat.focus.finding.which-documents',
    'chat.focus.finding.how-to-resolve',
  ],
  obligation: [
    'chat.focus.obligation.next-due',
    'chat.focus.obligation.what-proves-it',
    'chat.focus.obligation.catch-up',
  ],
  register_entry: [
    'chat.focus.register-entry.explain',
    'chat.focus.register-entry.where-it-stands',
    'chat.focus.register-entry.what-next',
  ],
  // MYAC-1162 — a document the assistant wrote. Read-side questions only: the
  // chips SEND on press, and a chip that rewrites the document would edit it
  // before the user had said what they wanted changed. Asking for a change is
  // what the input below them is for.
  authored_document: [
    'chat.focus.authored-document.what-it-says',
    'chat.focus.authored-document.whats-missing',
    'chat.focus.authored-document.improve',
  ],
  // MYAC-1127 — a board task. The three questions mirror what the chat can
  // actually do with one: read it, draft the content of its actions, and change
  // it. A set the tools cannot answer would be an invitation to a dead end.
  task: [
    'chat.focus.task.what-it-asks',
    TASK_DRAFT_CONTENT_KEY,
    'chat.focus.task.where-it-came-from',
  ],
}

/**
 * MYAC-1166 — the task dialog's « ask the assistant to draft it », on an action
 * that carries no suggested content. A sibling of `TASK_DRAFT_CONTENT_KEY`
 * and NOT that key: the chip asks for the whole task's content, while the
 * button sits on ONE action and the chat addresses an action by its document's
 * stored name — so the sentence has to carry `{document}`. Same verb, same
 * register, kept here beside the chip so the two readings cannot drift apart.
 */
export const TASK_DRAFT_CONTENT_FOR_KEY = 'chat.focus.task.draft-content-for'

/**
 * The same ask on an assessment proposal not yet accepted (MYAC-1418): no board
 * task to attach, so the sentence names the proposal itself.
 */
export const TASK_DRAFT_CONTENT_FOR_PROPOSAL_KEY = 'chat.focus.task.draft-content-for-proposal'

/** i18n keys of the questions offered for this subject, in order. */
export function focusSuggestionKeys(focus: ChatFocusRef): string[] {
  return BY_KIND[focus.kind] ?? []
}

/** The chip's text: the caller's label, or the identity it was built from. */
export function focusChipLabel(focus: ChatFocusRef): string {
  return focus.label || focus.name || focus.code || focus.uid || ''
}
